/**
 * Policy Comparison, as its own front door.
 *
 * Josh, 2026-09-07: "Let's make it its own tool. People may want to use it
 * stand alone. But we could also incorporate into the Account Inspector tool as
 * it probably is now."
 *
 * ⚠️ THE SAME ENGINE AS THE INSPECTOR'S PANEL, NOT A SECOND ONE. Both read the
 * same account, the same two sides, and the same server-computed diff; this
 * renders the identical <PolicyComparison>. Writing a second implementation
 * would have been the obvious mistake — two answers to "did the umbrella
 * survive the renewal", disagreeing the first time one of them was fixed.
 *
 * What differs is the way in. The Inspector assumes you came to analyse an
 * account and ended up comparing; this assumes comparing is the whole job, so
 * it asks for an insured and two sets of documents and gets out of the way.
 *
 * ⚠️ AND IT LISTS ONLY ACCOUNTS THAT HAVE TWO SIDES. Every Inspector account
 * would otherwise appear here as something you could compare, which is untrue
 * of most of them and turns the list into the Inspector's list wearing a
 * different title.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { ApiError, api, type InspectorAccount } from '../lib/api';

/** The two sides a comparison starts with, named the way an agent would. */
const LEFT = 'Expiring';
const RIGHT = 'Proposed';

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** How many sides an account has declared, and how many have been read. */
function sideStats(a: InspectorAccount): { sides: number; read: number } {
  const declared = a.data?.sides ?? [];
  if (!declared.length) return { sides: 1, read: a.data?.extraction ? 1 : 0 };
  const primaryIndex = Math.max(0, declared.findIndex((s) => s.id === 'primary'));
  const read = declared.filter(
    (s, i) => s.extractedAt || (i === primaryIndex && a.data?.extraction),
  ).length;
  return { sides: declared.length, read };
}

export function Compare(): JSX.Element {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<InspectorAccount[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [insured, setInsured] = useState('');

  const load = () =>
    api
      .inspectorAccounts()
      .then((r) => setAccounts(r.accounts))
      .catch((e: unknown) => {
        setAccounts([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load comparisons.');
      });

  useEffect(() => {
    void load();
  }, []);

  const rows = useMemo(
    () =>
      (accounts ?? [])
        .map((a) => ({ a, ...sideStats(a) }))
        // Two sides declared is what makes something a comparison at all.
        .filter((r) => r.sides > 1),
    [accounts],
  );

  /**
   * Start one.
   *
   * ⚠️ IT RENAMES THE PRIMARY SIDE, WHICH IS THE ONLY REASON THAT ENDPOINT
   * EXISTS. A fresh account's primary side is called "This account" — a sensible
   * default and a meaningless column heading. A comparison opening with
   * "This account" against "Proposed" would make somebody guess which one is
   * the renewal, which is the one thing this page must never leave ambiguous.
   */
  const start = async () => {
    const name = insured.trim();
    if (!name) return;
    setBusy(true);
    setErr('');
    try {
      const { account } = await api.inspectorCreateAccount({ namedInsured: name });
      await api.inspectorRenameSide(account.id, 'primary', LEFT);
      await api.inspectorAddSide(account.id, RIGHT);
      navigate(`/hub/compare/${account.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start that comparison.');
      setBusy(false);
    }
  };

  return (
    <div className="wrap">
      <PageHero
        title="Policy Comparison"
        sub="Put the expiring policy against the proposed one and see what changed — line by line, from the documents themselves."
      />

      {err && <div className="empty">{err}</div>}

      <section className="panel cmp-start">
        <div className="pu-head">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5h6v14H4zM14 5h6v14h-6zM10 12h4" />
          </svg>
          <div>
            <h3>Start a comparison</h3>
            <p>
              Name the insured, then drop the expiring policy on one side and the proposed one on
              the other. Everything is read from the documents — nothing is typed twice.
            </p>
          </div>
        </div>
        <div className="cmp-start-row">
          <input
            type="text"
            value={insured}
            onChange={(e) => setInsured(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void start();
            }}
            placeholder="Named insured"
            aria-label="Named insured"
          />
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy || !insured.trim()}
            onClick={() => void start()}
          >
            {busy ? 'Starting…' : 'Start'}
          </button>
        </div>
      </section>

      <section className="panel lr-table-panel">
        {accounts === null ? (
          <p className="lr-empty">Loading…</p>
        ) : !rows.length ? (
          <p className="lr-empty">
            No comparisons yet.
            <span>
              Start one above, or open an Account Inspector account and add a side to compare
              against — both end up in the same place.
            </span>
          </p>
        ) : (
          <table className="lr-table">
            <thead>
              <tr>
                <th>Insured</th>
                <th>Sides</th>
                <th>Read</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ a, sides, read }) => (
                <tr
                  key={a.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/hub/compare/${a.id}`)}
                >
                  <td>
                    <b style={{ fontWeight: 600 }}>{a.named_insured || 'Unnamed'}</b>
                    <div className="lr-sub">{a.id}</div>
                  </td>
                  <td>{sides}</td>
                  <td>
                    {/*
                      Two sides read is the line between "a comparison" and "a
                      comparison you cannot see yet", and it is the thing
                      somebody scanning this list actually wants to know.
                    */}
                    <span className={`tag ${read >= 2 ? 'is-online' : 'is-draft'}`}>
                      {read >= 2 ? 'Ready' : `${read} of ${sides} read`}
                    </span>
                  </td>
                  <td className="lr-sub">{shortDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel lr-about">
        <div>
          <h3>What it compares</h3>
          <ul>
            <li>Lines that are on one side and not the other — sorted first</li>
            <li>Carrier changes, and premium moves per line</li>
            <li>Limits, shown exactly as each policy states them</li>
            <li>Schedules that grew or shrank — vehicles, locations, drivers</li>
          </ul>
        </div>
        <div>
          <h3>What it will not tell you</h3>
          <p>
            Whether coverage got better or worse. Limits are one line of free text on a policy, so
            they are shown side by side and marked as different — never judged. Deductibles,
            sublimits, endorsements and form numbers are not extracted at all, so read the forms
            before advising anyone.
          </p>
        </div>
      </section>
    </div>
  );
}
